/**
 * metaballVolPipeline — a 2D projection of a 3D metaball field with
 * specular highlights and colored internal glow.
 *
 * 40 metaballs drift in 3D-style Lissajous orbits. Each ball
 * contributes an inverse-squared falloff onto a scalar density field;
 * the field is iso-surfaced at threshold = 1.0 via smoothstep. We
 * compute a pseudo-normal from the field gradient for the specular
 * pass, and add Fresnel-like rim glow using the dot product of the
 * gradient with view.
 *
 * The metaballs separate and re-coalesce over time, never stay still.
 * Audio pushes the blend threshold and the specular intensity.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface MetaballVolPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise2(p);
    p *= 2.04; a *= 0.5;
  }
  return v;
}

#define NBALLS 24

// Compute metaball field at 2D position p, with audio reactive radius.
float metaField(vec2 p, float t, float bass, float mid) {
  float field = 0.0;
  for (int i = 0; i < NBALLS; i++) {
    float fi = float(i);
    // 3D-ish Lissajous orbits — splay out across the plane in 2D too.
    float ax = 0.95 + 0.25 * hash21(vec2(fi, 11.0));
    float ay = 0.60 + 0.30 * hash21(vec2(fi, 13.0));
    float phx = hash21(vec2(fi, 17.0)) * 6.2832;
    float phy = hash21(vec2(fi, 19.0)) * 6.2832;
    float r1 = 1.7 + 0.4 * hash21(vec2(fi, 23.0));
    float r2 = 1.3 + 0.5 * hash21(vec2(fi, 29.0));
    vec2 c = vec2(
      ax * sin(r1 * t + phx),
      ay * sin(r2 * t + phy)
    );
    // Bass swells balls slightly.
    float r = 0.07 + 0.04 * hash21(vec2(fi, 31.0))
                 + 0.020 * mid * hash21(vec2(fi, 37.0));
    vec2 d = p - c;
    float dd = dot(d, d) + 0.003;
    field += (r * r) / dd;
  }
  return field;
}

// Gradient (approximate) of the field.
vec2 metaGrad(vec2 p, float t, float bass, float mid) {
  float e = 0.003;
  float fx = metaField(p + vec2(e, 0.0), t, bass, mid);
  float fxn = metaField(p - vec2(e, 0.0), t, bass, mid);
  float fy = metaField(p + vec2(0.0, e), t, bass, mid);
  float fyn = metaField(p - vec2(0.0, e), t, bass, mid);
  return vec2(fx - fxn, fy - fyn) / (2.0 * e);
}

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime * 0.45;

  // Background: dark teal with magenta nebula haze + soft grid.
  vec3 bgDeep = vec3(0.010, 0.020, 0.045);
  vec3 bgMid  = vec3(0.055, 0.030, 0.090);
  float rd = length(p);
  vec3 col = mix(bgDeep, bgMid, smoothstep(0.0, 1.0, rd));
  col += vec3(0.06, 0.04, 0.10) * fbm(p * 2.0 + vec2(t * 0.05, -t * 0.03));
  // Subtle hexagonal grid hints at "volumetric" feel.
  vec2 gp = p * 6.0;
  float hex = 0.5 + 0.5 * sin(gp.x + gp.y + t * 1.7);
  col += vec3(0.02, 0.03, 0.05) * hex * 0.3;

  // Field and its gradient.
  float fld = metaField(p, t, bass, mid);
  vec2 grad = metaGrad(p, t, bass, mid);
  // Surface threshold decreases slightly with bass — so balls appear to swell.
  float thresh = 0.25 - bass * 0.05;
  float density = smoothstep(thresh - 0.06, thresh + 0.06, fld);

  // Body color: multiple tints blended by which ball contributes most.
  vec3 ballA = vec3(1.00, 0.25, 0.45); // hot pink
  vec3 ballB = vec3(0.20, 0.90, 0.65); // aqua
  vec3 ballC = vec3(0.55, 0.30, 0.95); // violet
  vec3 ballD = vec3(1.00, 0.85, 0.30); // gold
  // Mix color zones by position seed.
  vec3 ballCol = mix(ballA, ballB, 0.5 + 0.5 * sin(p.x * 4.0 + p.y * 3.0 + t * 0.7));
  ballCol = mix(ballCol, ballC, 0.5 + 0.5 * cos(p.x * 3.0 - p.y * 2.0 + t * 0.5));
  ballCol = mix(ballCol, ballD, high * 0.4 + bass * 0.2);

  // Internal glow: rise with field value above threshold.
  float glow = smoothstep(thresh + 0.05, thresh + 0.20, fld);
  vec3 bodyCol = ballCol * (0.40 + 1.2 * glow + bass * 0.6);

  col = mix(col, bodyCol, density);

  // Specular highlight — bright spot at highest field crests with
  // gradient magnitude near zero (sphere top).
  float gradMag = length(grad);
  float specHard = smoothstep(15.0, 25.0, gradMag) * (1.0 - smoothstep(28.0, 35.0, gradMag));
  // Direction toward camera is implicit; we use gradient direction.
  float specExp = pow(specHard, 6.0);
  vec3 specCol = vec3(1.0, 0.95, 0.85);
  col += specCol * specExp * (0.85 + high * 1.8 + bass * 0.6);

  // Fresnel-like rim: edge of balls (density near 0.5).
  float rim = smoothstep(0.30, 0.50, density) * (1.0 - smoothstep(0.55, 0.75, density));
  vec3 rimCol = mix(ballC, ballA, 0.5 + 0.5 * sin(t * 0.6));
  col += rimCol * rim * (0.7 + mid * 1.5);

  // Bubble interior highlights as light plays across the surface.
  vec2 highlightDir = vec2(cos(t * 1.7), sin(t * 1.3)) * 0.4;
  float band = pow(clamp(dot(normalize(grad + 1e-3), highlightDir), 0.0, 1.0), 8.0);
  col += vec3(1.0, 0.95, 0.78) * band * density * (0.5 + bass * 1.0);

  // Audio overall tint.
  col += vec3(0.10, 0.04, 0.12) * bass * 0.25
       + vec3(0.05, 0.12, 0.10) * mid  * 0.20
       + vec3(0.12, 0.10, 0.05) * high * 0.15;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeMetaballVolBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.2);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.7 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.4 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.4 + 2.1);
}

export function computeMetaballVolRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createMetaballVolPipeline(gl: WebGL2RenderingContext): MetaballVolPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawMetaballVol(
  gl: WebGL2RenderingContext,
  pipe: MetaballVolPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
